import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * Breadcrumbs — visible breadcrumb trail rendered as native <a href> anchors.
 * Complements the JSON-LD BreadcrumbList schema emitted via JsonLd component.
 *
 * Props:
 *  - trail: Array<{name: string, path: string}>
 *      Example: [{name:"Accueil", path:"/"}, {name:"Prestations", path:"/prestations"}, {name:"Terrassement", path:"/prestations/terrassement"}]
 *  - variant: "light" | "onImage" — controls contrast for hero backgrounds
 */
export default function Breadcrumbs({ trail, variant = "light" }) {
    if (!trail || trail.length === 0) return null;

    const isOnImage = variant === "onImage";
    const baseColor = isOnImage ? "text-white/85" : "text-navy-700/75";
    const currentColor = isOnImage ? "text-white" : "text-navy-700";
    const sepColor = isOnImage ? "text-white/45" : "text-navy-700/40";

    return (
        <nav
            aria-label="Fil d'Ariane"
            className="w-full"
            data-testid="breadcrumbs"
        >
            <ol
                className={`flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] leading-tight ${baseColor}`}
                itemScope
                itemType="https://schema.org/BreadcrumbList"
            >
                {trail.map((step, i) => {
                    const isLast = i === trail.length - 1;
                    return (
                        <li
                            key={step.path}
                            className="inline-flex items-center gap-1.5"
                            itemProp="itemListElement"
                            itemScope
                            itemType="https://schema.org/ListItem"
                        >
                            {isLast ? (
                                <span
                                    className={`font-semibold ${currentColor}`}
                                    aria-current="page"
                                    itemProp="name"
                                    data-testid={`breadcrumb-current`}
                                >
                                    {step.name}
                                </span>
                            ) : (
                                <Link
                                    to={step.path}
                                    className={`hover:underline underline-offset-2 ${
                                        isOnImage
                                            ? "hover:text-white"
                                            : "hover:text-navy-700"
                                    }`}
                                    itemProp="item"
                                    data-testid={`breadcrumb-link-${i}`}
                                >
                                    <span itemProp="name">{step.name}</span>
                                </Link>
                            )}
                            <meta itemProp="position" content={String(i + 1)} />
                            {!isLast && (
                                <ChevronRight
                                    className={`w-3.5 h-3.5 ${sepColor}`}
                                    aria-hidden="true"
                                />
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}
